import React, { useState, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  FileText, CheckCircle, XCircle, AlertTriangle,
  Copy, Download, ChevronDown, ChevronRight, Search, X
} from 'lucide-react';

// ─── Collapsible section wrapper ──────────────────────────────────────────────
function Section({ id, title, icon, badge, borderColor, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      id={id}
      className="card"
      style={{ marginBottom: '12px', borderColor: borderColor || 'var(--border)' }}
    >
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          cursor: 'pointer', userSelect: 'none',
          marginBottom: open ? '12px' : 0
        }}
      >
        {open
          ? <ChevronDown size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          : <ChevronRight size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />}
        {icon}
        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)' }}>
          {title}
        </span>
        {badge}
      </div>
      {open && children}
    </div>
  );
}

// ─── Summary stats with jump-to ───────────────────────────────────────────────
function SummaryStats({ summary }) {
  if (!summary) return null;

  function jumpTo(id) {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  const cards = [
    {
      color: 'cyan', label: 'Total Records',
      value: summary.totalRecords,
      sub: `${summary.uniqueSsps} unique SSPs`,
      target: 'section-valid'
    },
    {
      color: 'green', label: 'Valid',
      value: summary.validRecords,
      sub: `${summary.directEntries} DIRECT · ${summary.resellerEntries} RESELLER`,
      target: 'section-valid', valueColor: 'green'
    },
    {
      color: 'amber', label: 'Warnings',
      value: summary.warningRecords || 0,
      sub: summary.tagIdUnverifiable > 0
        ? `${summary.tagIdUnverifiable} TAG-ID unverifiable`
        : 'Advisory issues on valid records',
      target: 'section-warnings', valueColor: 'amber'
    },
    {
      color: 'red', label: 'Invalid',
      value: summary.invalidRecords,
      // Only show what actually contributes to invalid count — not duplicates (those are separate)
      sub: summary.tagIdInvalid > 0
        ? `incl. ${summary.tagIdInvalid} bad TAG-IDs`
        : 'Format / field errors',
      target: 'section-invalid', valueColor: 'red'
    },
    {
      color: 'purple', label: 'Duplicates',
      value: summary.duplicates,
      sub: 'Repeated entries (domain+ID+type)',
      target: 'section-duplicates', valueColor: 'purple'
    },
  ];

  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'8px', marginBottom:'16px' }}>
      {cards.map(({ color, label, value, sub, target, valueColor }) => (
        <div
          key={label}
          className={`stat-card ${color}`}
          onClick={() => jumpTo(target)}
          style={{ cursor: 'pointer', transition: 'transform 0.1s' }}
          onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
          title={`Click to jump to ${label} section`}
        >
          <div className="stat-label">{label}</div>
          <div className={`stat-value ${valueColor || ''}`}>{value}</div>
          <div className="stat-sub">{sub}</div>
          <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            ↓ click to jump
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Records table with pagination ────────────────────────────────────────────
function RecordsTable({ records, title, variant = 'valid', id, searchQuery = '', filterFn }) {
  const [page, setPage] = useState(0);
  const PER_PAGE = 50;

  // Apply search filter
  const filtered = (filterFn && searchQuery)
    ? records.filter(r => filterFn(r, searchQuery))
    : records;

  // Reset to page 0 when search changes
  React.useEffect(() => { setPage(0); }, [searchQuery]);

  const paged = filtered.slice(page * PER_PAGE, (page + 1) * PER_PAGE);

  if (!records || records.length === 0) return null;

  return (
    <Section
      id={id}
      title={title}
      icon={variant === 'valid'
        ? <CheckCircle size={14} style={{ color: 'var(--green)' }} />
        : <XCircle size={14} style={{ color: 'var(--red)' }} />}
      badge={<span className="badge badge-muted">{searchQuery && filtered.length !== records.length ? `${filtered.length}/${records.length}` : records.length}</span>}
      borderColor={variant === 'invalid' ? 'color-mix(in srgb, var(--red) 30%, transparent)' : undefined}
      defaultOpen={variant === 'valid'}
    >
      {/* Search-filtered count + pagination */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px', minHeight:'24px' }}>
        {searchQuery && filtered.length !== records.length && (
          <span style={{ fontSize:'11px', color:'var(--cyan)' }}>
            Showing {filtered.length} of {records.length} records matching "{searchQuery}"
          </span>
        )}
        {filtered.length > PER_PAGE && (
          <div style={{ display:'flex', gap:'6px', alignItems:'center', fontSize:'11px', color:'var(--text-muted)', marginLeft:'auto' }}>
            Page {page + 1} of {Math.ceil(filtered.length / PER_PAGE)}
            <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>‹</button>
            <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => Math.min(Math.ceil(filtered.length / PER_PAGE) - 1, p + 1))} disabled={(page + 1) * PER_PAGE >= filtered.length}>›</button>
          </div>
        )}
      </div>
      {searchQuery && filtered.length === 0 && (
        <div style={{ padding:'20px', textAlign:'center', color:'var(--text-muted)', fontSize:'13px' }}>
          No records match "{searchQuery}"
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>#</th>
              <th>SSP Domain</th>
              <th>Publisher ID</th>
              <th>Type</th>
              <th>TAG-ID</th>
              {variant === 'invalid' && <th>Error</th>}
              {variant === 'valid' && <th>TAG-ID Status</th>}
            </tr>
          </thead>
          <tbody>
            {paged.map((r, i) => (
              <tr key={i}>
                <td className="text-muted">{r.lineNumber}</td>
                <td style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{r.domain || '—'}</td>
                <td className="mono">{r.publisherAccountId || '—'}</td>
                <td>
                  {r.accountType && (
                    <span className={`badge ${r.accountType === 'DIRECT' ? 'badge-cyan' : 'badge-purple'}`}>
                      {r.accountType}
                    </span>
                  )}
                </td>
                <td>
                  {r.certAuthorityId ? (
                    <div style={{ display:'flex', flexDirection:'column', gap:'3px' }}>
                      <span className="mono" style={{ fontSize:'11px', color:'var(--text-muted)' }}>{r.certAuthorityId}</span>
                      {r.tagIdStatus === 'verified'     && <span className="badge badge-green" style={{ fontSize:'9px', width:'fit-content' }}>✓ Verified</span>}
                      {r.tagIdStatus === 'invalid'      && <span className="badge badge-red"   style={{ fontSize:'9px', width:'fit-content' }}>✗ Not in sellers.json</span>}
                      {r.tagIdStatus === 'unverifiable' && <span className="badge badge-amber" style={{ fontSize:'9px', width:'fit-content' }}>? Unverifiable</span>}
                    </div>
                  ) : (
                    <span style={{ opacity:0.3 }}>—</span>
                  )}
                </td>
                {variant === 'invalid' && (
                  <td style={{ color: 'var(--red)', fontSize: '11px' }}>
                    {r.error || (r.errors && r.errors.join('; '))}
                  </td>
                )}
                {variant === 'valid' && (
                  <td>
                    {r.tagIdStatus === 'verified'
                      ? <span className="badge badge-green" style={{ fontSize:'9px' }}>✓ Verified</span>
                      : r.certAuthorityId
                        ? <span style={{ opacity:0.3, fontSize:'11px' }}>—</span>
                        : <span style={{ opacity:0.3 }}>—</span>}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

// ─── Inventory Partners ───────────────────────────────────────────────────────
// IAB ads.txt 1.1 defines `inventorypartnerdomain=` as a VARIABLE line: a
// publisher declaring that some inventory here is sold by another entity. This
// answers, in bulk, "which of these publishers name MY partner domain?" — and
// shows every partner they DO name, which is often the more useful answer when
// yours is missing.
//
// Self-contained state so the rest of the page is untouched.

// Domains are one fetch each; bundles are a live STORE LOOKUP each and are far
// slower. 25 bundles measured at 30.65s and hit the gateway limit, so in-app goes
// out in fives — 50 quick requests instead of 10 that die.
const IP_BATCH_WEB   = 25;   // must not exceed IP_BATCH_MAX on the server
// Was 5, set when a 25-bundle batch appeared to time out — that was a corrupted
// connection, not real work. 15 cuts 250 bundles from 50 round-trips to 17 while
// staying inside the server's 20s deadline. Check elapsedMs before raising it.
const IP_BATCH_INAPP = 15;
const IP_MAX   = 250;

const IP_STATUS = {
  found:          { label: 'Declares you',      color: 'var(--green)',          hint: 'inventorypartnerdomain matches the domain you entered' },
  other_partners: { label: 'Other partners',    color: 'var(--amber)',          hint: 'Declares inventory partners, but not yours' },
  not_found:      { label: 'No declarations',   color: 'var(--text-secondary)', hint: 'The file has no inventorypartnerdomain lines at all' },
  blocked:        { label: 'Blocked',           color: 'var(--amber)',          hint: 'Bot protection served a challenge instead of the file — unknown, not absent' },
  unreachable:    { label: 'Unreachable',       color: 'var(--text-secondary)', hint: 'The file could not be fetched' },
  unresolved:     { label: 'Unresolved',        color: 'var(--red)',            hint: 'That bundle could not be resolved to a developer domain' },
  timed_out:      { label: 'Not checked',       color: 'var(--amber)',          hint: 'The batch hit its time limit before reaching this one — retry it' },
};

function InventoryPartnersTab() {
  const [mode, setMode]       = useState('web');          // 'web' | 'inapp'
  const [partner, setPartner] = useState('');
  const [webInput, setWebInput]     = useState('');
  const [webType, setWebType]       = useState('ads.txt');
  const [appInput, setAppInput]     = useState('');
  const [appType, setAppType]       = useState('app-ads.txt');   // IAB default for in-app
  const [rows, setRows]       = useState([]);
  const [busy, setBusy]       = useState(false);
  const [done, setDone]       = useState(0);
  const [total, setTotal]     = useState(0);
  const [err, setErr]         = useState(null);
  const [filter, setFilter]   = useState('all');

  const input    = mode === 'web' ? webInput : appInput;
  const setInput = mode === 'web' ? setWebInput : setAppInput;
  const type     = mode === 'web' ? webType : appType;
  const setType  = mode === 'web' ? setWebType : setAppType;

  // One per line or comma-separated, deduped, capped. Pasting a column out of a
  // spreadsheet should just work.
  const parseInputs = (txt) => {
    const out = [];
    String(txt || '').split(/[\n,;]+/).forEach(v => {
      const t = v.trim();
      if (t && !out.includes(t)) out.push(t);
    });
    return out;
  };
  const parsed = parseInputs(input);

  const run = async () => {
    const list = parsed.slice(0, IP_MAX);
    if (!partner.trim()) { toast.error('Enter the inventorypartnerdomain value to look for'); return; }
    if (!list.length)    { toast.error(mode === 'web' ? 'Paste at least one domain' : 'Paste at least one bundle ID'); return; }

    setBusy(true); setErr(null); setRows([]); setDone(0); setTotal(list.length);
    const acc = [];
    try {
      // Sequential batches. Ten requests of 25 rather than one of 250: a single
      // long request would time out at the gateway, and this fills the table as
      // it goes instead of showing nothing for a minute and a half.
      const step = mode === 'web' ? IP_BATCH_WEB : IP_BATCH_INAPP;
      for (let i = 0; i < list.length; i += step) {
        const chunk = list.slice(i, i + step);
        const { data } = await axios.post('/api/ads-txt/inventory-partners', {
          mode: mode === 'web' ? 'domain' : 'bundle',
          type, partnerDomain: partner.trim(), inputs: chunk,
        });
        acc.push(...(data.rows || []));
        setRows(acc.slice());
        setDone(Math.min(list.length, i + chunk.length));
      }
    } catch (e) {
      // Keep whatever landed. A failed batch nine should not discard batches one
      // to eight.
      setErr((e.response && e.response.data && e.response.data.error) || e.message);
    }
    setBusy(false);
  };

  const shown = rows.filter(r => filter === 'all'
    || (filter === 'found' && r.status === 'found')
    || (filter === 'missing' && (r.status === 'other_partners' || r.status === 'not_found'))
    || (filter === 'problem' && (r.status === 'blocked' || r.status === 'unreachable' || r.status === 'unresolved' || r.status === 'timed_out')));

  const counts = rows.reduce((a, r) => { a[r.status] = (a[r.status] || 0) + 1; return a; }, {});

  const exportCsv = () => {
    const head = ['input', 'resolved_domain', 'file', 'status', 'declares_your_domain', 'all_partners_declared', 'via_unblocker', 'error'];
    const lines = [head.join(',')].concat(rows.map(r => [
      r.input, r.resolvedDomain || '', r.file, r.status, r.matched ? 'yes' : 'no',
      (r.declared || []).join(' | '), r.viaUnblocker ? 'yes' : 'no', (r.error || '').replace(/[",\n]/g, ' '),
    ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `inventory-partners-${partner.trim() || 'export'}.csv`;
    a.click(); URL.revokeObjectURL(a.href);
  };

  return (
    <div>
      <div className="card mb-4">
        <div className="tabs" style={{ marginBottom: 14 }}>
          <button className={`tab ${mode === 'web' ? 'active' : ''}`} onClick={() => setMode('web')}>Web</button>
          <button className={`tab ${mode === 'inapp' ? 'active' : ''}`} onClick={() => setMode('inapp')}>In-App</button>
        </div>

        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 0, marginBottom: 14 }}>
          Paste up to {IP_MAX} {mode === 'web' ? 'domains' : 'bundle IDs or store URLs'}, one per line. Each file is
          checked for <code>inventorypartnerdomain=</code> lines, and we report whether yours is among them — along
          with every partner domain the file does declare.
          {mode === 'inapp' ? ' Bundles are resolved to a developer domain first, and that domain is shown on every row.' : ''}
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 320px)', gap: 14, marginBottom: 12, alignItems: 'start' }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 5 }}>{mode === 'web' ? 'Domains' : 'Bundle IDs or store URLs'}</div>
            <textarea className="input-field" rows={7} value={input} onChange={e => setInput(e.target.value)}
                      placeholder={mode === 'web' ? 'example.com\nanother.com' : 'com.example.game\n1234567890'}
                      style={{ fontFamily: 'var(--font-mono)', fontSize: 12, width: '100%', boxSizing: 'border-box', resize: 'vertical' }} />
            <div style={{ fontSize: 12, color: parsed.length > IP_MAX ? 'var(--red)' : 'var(--text-secondary)', marginTop: 4 }}>
              {parsed.length} entered{parsed.length > IP_MAX ? ` \u2014 only the first ${IP_MAX} will be checked` : ''}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 5 }}>inventorypartnerdomain to find</div>
            <input className="input-field" value={partner} onChange={e => setPartner(e.target.value)}
                   placeholder="yourpartner.com"
                   style={{ fontFamily: 'var(--font-mono)', width: '100%', boxSizing: 'border-box' }} />

            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginTop: 12, marginBottom: 5 }}>File to check</div>
            <select className="select-field" value={type} onChange={e => setType(e.target.value)} style={{ width: '100%' }}>
              <option value="ads.txt">ads.txt</option>
              <option value="app-ads.txt">app-ads.txt</option>
            </select>

            <button className="btn btn-primary" onClick={run} disabled={busy}
                    style={{ marginTop: 12, width: '100%', boxSizing: 'border-box', justifyContent: 'center' }}>
              {busy ? `Checking ${done}/${total}\u2026` : <><Search size={15} /> Check {parsed.length ? Math.min(parsed.length, IP_MAX) : ''}</>}
            </button>
          </div>
        </div>

        {busy && (
          <div style={{ height: 4, background: 'var(--bg-surface)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${total ? Math.round((done / total) * 100) : 0}%`,
                          background: 'var(--accent)', transition: 'width 200ms ease' }} />
          </div>
        )}

        {err && (
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--red)' }}>
            <AlertTriangle size={13} /> {err}
            {rows.length ? ` \u2014 showing the ${rows.length} results that completed before the failure.` : ''}
          </div>
        )}
      </div>

      {rows.length > 0 && (
        <div className="card">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
            {[['all', `All ${rows.length}`], ['found', `Declares you ${counts.found || 0}`],
              ['missing', `Missing ${(counts.other_partners || 0) + (counts.not_found || 0)}`],
              ['problem', `Couldn\u2019t check ${(counts.blocked || 0) + (counts.unreachable || 0) + (counts.unresolved || 0) + (counts.timed_out || 0)}`]
            ].map(([v, label]) => (
              <button key={v} className={`btn btn-sm ${filter === v ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter(v)}>{label}</button>
            ))}
            <span style={{ marginLeft: 'auto' }} />
            <button className="btn btn-sm btn-secondary" onClick={exportCsv}><Download size={13} /> CSV</button>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ fontSize: 12 }}>
              <thead>
                <tr>
                  <th>{mode === 'web' ? 'Domain' : 'Bundle'}</th>
                  {mode === 'inapp' && <th>Resolved domain</th>}
                  <th>Status</th>
                  <th>Partner domains declared in this file</th>
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && (
                  <tr><td colSpan={mode === 'inapp' ? 4 : 3} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '14px 0' }}>
                    Nothing in this filter.
                  </td></tr>
                )}
                {shown.map((r, i) => {
                  const st = IP_STATUS[r.status] || IP_STATUS.unreachable;
                  return (
                    <tr key={`${r.input}-${i}`}>
                      <td style={{ fontFamily: 'var(--font-mono)' }}>{r.input}</td>
                      {mode === 'inapp' && (
                        <td style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)' }}>{r.resolvedDomain || '\u2014'}</td>
                      )}
                      <td>
                        <span style={{ color: st.color, fontWeight: 600 }} title={r.error || st.hint}>{st.label}</span>
                        {r.viaUnblocker ? <span className="badge badge-muted" style={{ fontSize: 10, marginLeft: 6 }}
                                                title="Fetched through the bot-protection unblocker">via proxy</span> : null}
                      </td>
                      <td>
                        {(r.declared || []).length === 0
                          ? <span style={{ color: 'var(--text-muted)' }}>—</span>
                          : (
                            <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                              {r.declared.map(d => (
                                <span key={d} className="badge"
                                      style={{ fontSize: 10, fontFamily: 'var(--font-mono)',
                                               background: d === (partner || '').trim().toLowerCase().replace(/^www\./, '') ? 'var(--green)' : 'var(--bg-surface)',
                                               color: d === (partner || '').trim().toLowerCase().replace(/^www\./, '') ? 'var(--on-accent)' : 'var(--text-secondary)' }}>
                                  {d}
                                </span>
                              ))}
                            </span>
                          )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function AdsTxtChecker() {
  const [domain, setDomain] = useState('');
  const [type, setType] = useState('ads.txt');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [tab, setTab] = useState('check');
  const [rawContent, setRawContent] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  // ─── Tab 3: Check by Inapp Bundle ────────────────────────────────────────
  // Accepts an Android bundle ID, iOS app ID (numeric or id-prefixed), or full
  // store URL. Backend resolves to developer domain then runs the same fetch.
  const [bundleInput, setBundleInput] = useState('');
  const [bundleType,  setBundleType]  = useState('app-ads.txt');   // default per IAB spec

  // Freeform search — matches domain, publisher ID, TAG-ID, account type, raw line
  function filterRecord(r, q) {
    if (!q) return true;
    const lower = q.toLowerCase().trim();
    return (
      (r.domain             || '').toLowerCase().includes(lower) ||
      (r.publisherAccountId || '').toLowerCase().includes(lower) ||
      (r.certAuthorityId    || '').toLowerCase().includes(lower) ||
      (r.accountType        || '').toLowerCase().includes(lower) ||
      (r.raw                || '').toLowerCase().includes(lower)
    );
  }

  async function handleCheck() {
    if (!domain.trim()) return toast.error('Enter a domain first');
    setLoading(true); setResult(null); setSearchQuery('');
    try {
      const { data } = await axios.post('/api/ads-txt/check', { domain: domain.trim(), type });
      setResult(data);
      if (data.success) toast.success(`Found ${data.summary?.totalRecords} records`);
      else toast.error(data.error || 'Failed to fetch');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not fetch ads.txt — check the domain and try again');
    } finally { setLoading(false); }
  }

  async function handleParse() {
    if (!rawContent.trim()) return toast.error('Paste some content first');
    setLoading(true);
    try {
      const { data } = await axios.post('/api/ads-txt/parse', { content: rawContent });
      setResult({ ...data, url: 'Pasted content', success: true });
      toast.success(`Parsed ${data.summary?.totalRecords} records`);
    } catch (e) {
      toast.error('Parse error');
    } finally { setLoading(false); }
  }

  async function handleBundleCheck() {
    if (!bundleInput.trim()) return toast.error('Enter a bundle ID or store URL first');
    setLoading(true); setResult(null); setSearchQuery('');
    try {
      const { data } = await axios.post('/api/ads-txt/check-by-bundle', {
        input: bundleInput.trim(),
        type: bundleType,
      });
      setResult(data);
      if (data.success) {
        const resolved = data.resolution?.domain;
        toast.success(`Resolved → ${resolved} · ${data.summary?.totalRecords || 0} records`);
      } else {
        // 404 = couldn't resolve; 200 with success:false = resolved but fetch failed
        toast.error(data.error || data.resolution?.error || 'Could not check');
      }
    } catch (e) {
      const msg = e.response?.data?.error
        || e.response?.data?.resolution?.error
        || 'Could not resolve or fetch — verify the bundle ID / store URL is correct';
      toast.error(msg);
      // If we got a 404 with resolution metadata, still show it so the user knows what went wrong
      if (e.response?.data) setResult(e.response.data);
    } finally { setLoading(false); }
  }

  function downloadCSV() {
    if (!result?.records) return;
    const rows = [['Domain', 'Publisher ID', 'Account Type', 'TAG-ID', 'Line #']];
    result.records.forEach(r => rows.push([r.domain, r.publisherAccountId, r.accountType, r.certAuthorityId || '', r.lineNumber]));
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `adstxt-${domain || 'parsed'}.csv`; a.click();
  }

  return (
    <div>
      <h1 className="page-title">Ads.txt / App-ads.txt Checker</h1>
      <p className="page-subtitle">Validate IAB ads.txt and app-ads.txt files for format compliance, duplicates, and integrity</p>

      {/* Tabs */}
      <div className="tabs">
        <button className={`tab ${tab === 'check' ? 'active' : ''}`} onClick={() => setTab('check')}>Check by Domain</button>
        <button className={`tab ${tab === 'bundle' ? 'active' : ''}`} onClick={() => setTab('bundle')}>Check by Inapp Bundle</button>
        <button className={`tab ${tab === 'partners' ? 'active' : ''}`} onClick={() => setTab('partners')}>Inventory Partners</button>
        <button className={`tab ${tab === 'paste' ? 'active' : ''}`} onClick={() => setTab('paste')}>Paste & Parse</button>
      </div>

      {tab === 'partners' && <InventoryPartnersTab />}

      {tab === 'check' && (
        <div className="card mb-4">
          <div className="input-group">
            <input
              className="input-field"
              placeholder="publisher.com or app.example.com"
              value={domain}
              onChange={e => setDomain(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCheck()}
            />
            <select className="select-field" value={type} onChange={e => setType(e.target.value)}>
              <option value="ads.txt">ads.txt</option>
              <option value="app-ads.txt">app-ads.txt</option>
            </select>
            <button className="btn btn-primary" onClick={handleCheck} disabled={loading}>
              {loading ? <span className="spinner" /> : <FileText size={14} />}
              {loading ? 'Checking...' : 'Check'}
            </button>
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
            Enter just the domain (e.g. <code style={{ color: 'var(--cyan)' }}>example.com</code>) — no https:// needed
          </div>
        </div>
      )}

      {tab === 'bundle' && (
        <div className="card mb-4">
          <div className="input-group">
            <input
              className="input-field"
              placeholder="com.example.app · 123456789 · or full store URL"
              value={bundleInput}
              onChange={e => setBundleInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleBundleCheck()}
            />
            <select className="select-field" value={bundleType} onChange={e => setBundleType(e.target.value)}>
              <option value="app-ads.txt">app-ads.txt</option>
              <option value="ads.txt">ads.txt</option>
            </select>
            <button className="btn btn-primary" onClick={handleBundleCheck} disabled={loading}>
              {loading ? <span className="spinner" /> : <FileText size={14} />}
              {loading ? 'Resolving...' : 'Check'}
            </button>
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
            Accepts Android bundle IDs (<code style={{ color: 'var(--cyan)' }}>com.example.app</code>),
            iOS app IDs (<code style={{ color: 'var(--cyan)' }}>123456789</code> or <code style={{ color: 'var(--cyan)' }}>id123456789</code>),
            or full store URLs.
            We'll resolve the developer's website and check {bundleType} there.
          </div>
        </div>
      )}

      {tab === 'paste' && (
        <div className="card mb-4">
          <textarea
            className="input-field"
            style={{ minHeight: '180px' }}
            placeholder="Paste ads.txt content here..."
            value={rawContent}
            onChange={e => setRawContent(e.target.value)}
          />
          <div style={{ marginTop: '8px' }}>
            <button className="btn btn-primary" onClick={handleParse} disabled={loading}>
              {loading ? <span className="spinner" /> : <FileText size={14} />}
              {loading ? 'Parsing...' : 'Parse Content'}
            </button>
          </div>
        </div>
      )}

      {/* Results */}
      {result && (
        <>
          {/* Resolution banner — Sprint 3.6: shown when result came from bundle tab */}
          {result.resolution && (
            <div className="card" style={{ marginBottom: '12px', borderColor: result.resolution.domain ? 'var(--cyan)' : 'var(--red)' }}>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Resolved Input
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <code style={{ fontSize: '12px', color: 'var(--text-primary)' }}>{result.resolution.input}</code>
                <span style={{ color: 'var(--text-muted)' }}>→</span>
                {result.resolution.domain ? (
                  <code style={{ fontSize: '13px', color: 'var(--cyan)', fontWeight: 600 }}>{result.resolution.domain}</code>
                ) : (
                  <span style={{ fontSize: '12px', color: 'var(--red)' }}>could not resolve</span>
                )}
                {result.resolution.resolvedFrom && (
                  <span className="badge badge-muted">
                    {result.resolution.resolvedFrom === 'androidBundleId' && 'Android bundle'}
                    {result.resolution.resolvedFrom === 'iosAppId'        && 'iOS app ID'}
                    {result.resolution.resolvedFrom === 'storeUrl'        && 'Store URL'}
                  </span>
                )}
              </div>
              {(result.resolution.appName || result.resolution.sellerName) && (
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '6px' }}>
                  {result.resolution.appName && <span>App: <strong>{result.resolution.appName}</strong></span>}
                  {result.resolution.appName && result.resolution.sellerName && <span> · </span>}
                  {result.resolution.sellerName && <span>Developer: <strong>{result.resolution.sellerName}</strong></span>}
                </div>
              )}
              {result.resolution.source && (
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px', fontStyle: 'italic' }}>
                  via {result.resolution.source}
                </div>
              )}
              {!result.resolution.domain && result.error && (
                <div style={{ fontSize: '11px', color: 'var(--red)', marginTop: '6px' }}>
                  {result.error}
                </div>
              )}
            </div>
          )}

          {/* URL + actions header */}
          {(result.resolution?.domain || !result.resolution) && (
          <div className="card" style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {result.success
                  ? <CheckCircle size={16} style={{ color: 'var(--green)' }} />
                  : <XCircle size={16} style={{ color: 'var(--red)' }} />}
                <span style={{ fontWeight: 600 }}>{result.url}</span>
                {result.httpStatus && <span className="badge badge-muted">HTTP {result.httpStatus}</span>}
                {result.fetchTimeMs && <span className="badge badge-muted">{result.fetchTimeMs}ms</span>}
              </div>
              {result.success && (
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => { navigator.clipboard.writeText(result.rawContent || ''); toast.success('Copied!'); }}>
                    <Copy size={12} /> Copy Raw
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={downloadCSV}>
                    <Download size={12} /> Export CSV
                  </button>
                </div>
              )}
            </div>
            {!result.success && (
              <div className="alert alert-error" style={{ marginTop: '10px' }}>
                <XCircle size={14} /> {result.error}
              </div>
            )}
          </div>
          )}

          {result.success && result.summary && (
            <>
              {/* Clickable stats — jump to sections */}
              <SummaryStats summary={result.summary} />

              {/* Freeform search */}
              <div style={{ marginBottom:'12px', display:'flex', gap:'8px', alignItems:'center' }}>
                <div style={{ position:'relative', flex:1 }}>
                  <Search size={13} style={{ position:'absolute', left:'10px', top:'50%', transform:'translateY(-50%)', color:'var(--text-muted)', pointerEvents:'none' }}/>
                  <input
                    className="input-field"
                    style={{ paddingLeft:'32px', paddingRight: searchQuery ? '32px' : '12px' }}
                    placeholder="Search by domain, publisher ID, TAG-ID, or full line…"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')} style={{ position:'absolute', right:'8px', top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', padding:'2px', display:'flex', alignItems:'center' }}>
                      <X size={13}/>
                    </button>
                  )}
                </div>
                {searchQuery && (() => {
                  const allRecords = [...(result.records||[]), ...(result.invalidRecords||[]), ...(result.duplicates||[])];
                  const matches = allRecords.filter(r => filterRecord(r, searchQuery)).length;
                  return <span style={{ fontSize:'12px', color: matches > 0 ? 'var(--cyan)' : 'var(--text-muted)', whiteSpace:'nowrap', fontWeight:600 }}>{matches} match{matches !== 1 ? 'es' : ''}</span>;
                })()}
              </div>

              {/* Variable Declarations */}
              {result.variables && result.variables.length > 0 && (
                <Section
                  id="section-variables"
                  title="Variable Declarations"
                  icon={<AlertTriangle size={14} style={{ color: 'var(--amber)' }} />}
                  badge={<span className="badge badge-muted">{result.variables.length}</span>}
                  defaultOpen={true}
                >
                  {result.variables.map((v, i) => (
                    <div key={i} style={{ display: 'flex', gap: '8px', padding: '5px 0', borderBottom: '1px solid var(--border)', fontSize: '12px' }}>
                      <span style={{ color: 'var(--cyan)', minWidth: '180px' }}>{v.name}</span>
                      <span style={{ color: 'var(--text-secondary)' }}>{v.value}</span>
                      {!v.valid && <span className="badge badge-amber">{v.warning}</span>}
                    </div>
                  ))}
                </Section>
              )}

              {/* Valid Records */}
              <RecordsTable
                id="section-valid"
                records={result.records || []}
                title="Valid Records"
                variant="valid"
                searchQuery={searchQuery}
                filterFn={filterRecord}
              />

              {/* Warnings — valid records with advisory issues */}
              {(() => {
                const warningRecords = (result.records || []).filter(r => r.warnings && r.warnings.length > 0).filter(r => filterRecord(r, searchQuery));
                if (!warningRecords.length) return null;
                return (
                  <Section
                    id="section-warnings"
                    title="Warnings"
                    icon={<AlertTriangle size={14} style={{ color: 'var(--amber)' }} />}
                    badge={<span className="badge badge-amber">{warningRecords.length}</span>}
                    borderColor="color-mix(in srgb, var(--amber) 30%, transparent)"
                    defaultOpen={true}
                  >
                    <div style={{ fontSize:'11px', color:'var(--text-muted)', marginBottom:'10px', padding:'8px 10px', background:'color-mix(in srgb, var(--amber) 6%, transparent)', borderRadius:'var(--radius-sm)', border:'1px solid color-mix(in srgb, var(--amber) 20%, transparent)' }}>
                      These records are technically valid but have advisory issues. They won't break your ads.txt but may be worth reviewing.
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table className="data-table">
                        <thead>
                          <tr><th>#</th><th>SSP Domain</th><th>Publisher ID</th><th>Type</th><th>TAG-ID</th><th>Advisory</th></tr>
                        </thead>
                        <tbody>
                          {warningRecords.map((r, i) => (
                            <tr key={i}>
                              <td className="text-muted">{r.lineNumber}</td>
                              <td style={{ fontWeight:500, color:'var(--text-primary)' }}>{r.domain || '—'}</td>
                              <td className="mono">{r.publisherAccountId || '—'}</td>
                              <td>
                                {r.accountType && (
                                  <span className={`badge ${r.accountType === 'DIRECT' ? 'badge-cyan' : 'badge-purple'}`}>
                                    {r.accountType}
                                  </span>
                                )}
                              </td>
                              <td>
                                {r.certAuthorityId ? (
                                  <div style={{ display:'flex', flexDirection:'column', gap:'3px' }}>
                                    <span className="mono" style={{ fontSize:'11px', color:'var(--text-muted)' }}>{r.certAuthorityId}</span>
                                    {r.tagIdStatus === 'unverifiable' && <span className="badge badge-amber" style={{ fontSize:'9px', width:'fit-content' }}>? Unverifiable</span>}
                                  </div>
                                ) : <span style={{ opacity:0.3 }}>—</span>}
                              </td>
                              <td style={{ color:'var(--amber)', fontSize:'11px' }}>
                                {r.warnings && r.warnings[0]}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Section>
                );
              })()}

              {/* Invalid Records */}
              <RecordsTable
                id="section-invalid"
                records={result.invalidRecords || []}
                title="Invalid Records"
                variant="invalid"
                searchQuery={searchQuery}
                filterFn={filterRecord}
              />

              {/* Duplicates */}
              {result.duplicates && result.duplicates.length > 0 && (
                <Section
                  id="section-duplicates"
                  title="Duplicates"
                  icon={<AlertTriangle size={14} style={{ color: 'var(--amber)' }} />}
                  badge={<span className="badge badge-amber">{result.duplicates.length}</span>}
                  borderColor="color-mix(in srgb, var(--amber) 30%, transparent)"
                  defaultOpen={false}
                >
                  <table className="data-table">
                    <thead>
                      <tr><th>SSP Domain</th><th>Publisher ID</th><th>Type</th><th>Duplicate of Line</th></tr>
                    </thead>
                    <tbody>
                      {result.duplicates.filter(r => filterRecord(r, searchQuery)).map((r, i) => (
                        <tr key={i}>
                          <td>{r.domain}</td>
                          <td className="mono">{r.publisherAccountId}</td>
                          <td>
                            <span className={`badge ${r.accountType === 'DIRECT' ? 'badge-cyan' : 'badge-purple'}`}>
                              {r.accountType}
                            </span>
                          </td>
                          <td className="text-muted">Line {r.duplicateOf}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Section>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
